import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  Lock,
  Unlock,
  CheckCircle2,
  AlertTriangle,
  RefreshCw,
  Wand2,
  ListChecks,
} from 'lucide-react';
import { reconciliationAPI } from '../../../../api';
import Layout from '../../../../components/Layout';
import { Button } from '../../../../components/ui/Button';
import { Card, CardContent } from '../../../../components/ui/Card';
import { Input } from '../../../../components/ui/Input';
import { FormLabel } from '../../../../components/ui/FormLabel';
import { Badge } from '../../../../components/ui/Badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../../components/ui/Table';

/**
 * Month-end close workbench (plan §7 + §11).
 *
 * URL: /tenant/banking/reconciliation/accounts/:accountId/close
 *
 * This screen is the single surface a tenant uses to lock a bank /
 * card / loan period. It composes four loosely-coupled widgets that
 * each call a dedicated `reconciliation` endpoint:
 *
 *   1. Sessions list + "start new session" form
 *      → GET/POST /reconciliation/sessions
 *   2. Finalise/Reopen on the selected session
 *      → POST /reconciliation/sessions/:id/finalise|reopen
 *   3. Opening-balance mismatch wizard
 *      → GET /opening-balance-check + POST /opening-balance-fix
 *   4. Running-balance drift detector
 *      → GET /running-balance-drift
 *
 * The page is intentionally account-scoped — the tenant chooses an
 * account on the reconciliation hub, then drills in here to close the
 * period. Global status across all accounts lives on the hub tiles.
 */

const currency = (n) =>
  new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    minimumFractionDigits: 2,
  }).format(Number(n ?? 0));

const formatDate = (d) => {
  if (!d) return '—';
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return '—';
  return new Intl.DateTimeFormat('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  }).format(dt);
};

const toInputDate = (d) => new Date(d).toISOString().split('T')[0];

const STATUS_TONE = {
  in_progress: 'warning',
  reopened: 'warning',
  finalised: 'success',
};

export default function MonthEndClose() {
  const navigate = useNavigate();
  const { accountId } = useParams();
  const qc = useQueryClient();

  // Default period = previous calendar month — the most common use case.
  const today = useMemo(() => new Date(), []);
  const defaultStart = useMemo(
    () => new Date(today.getFullYear(), today.getMonth() - 1, 1),
    [today]
  );
  const defaultEnd = useMemo(
    () => new Date(today.getFullYear(), today.getMonth(), 0),
    [today]
  );

  const [newSession, setNewSession] = useState({
    period_start: toInputDate(defaultStart),
    period_end: toInputDate(defaultEnd),
    statement_closing_balance: '',
    notes: '',
  });
  const [selectedId, setSelectedId] = useState(null);
  const [reopenReason, setReopenReason] = useState('');
  const [forceFinalise, setForceFinalise] = useState(false);
  const [finaliseNotes, setFinaliseNotes] = useState('');

  const {
    data: sessionsData,
    isLoading: loadingSessions,
    error: sessionsError,
    refetch: refetchSessions,
  } = useQuery(
    ['reconciliation', 'sessions', accountId],
    () => reconciliationAPI.listSessions({ bank_account_id: accountId }),
    { enabled: !!accountId }
  );

  const sessions = sessionsData?.sessions || [];
  const selected = selectedId
    ? sessions.find((s) => s.id === selectedId) || null
    : sessions[0] || null;

  const { data: obCheck, refetch: refetchOb } = useQuery(
    ['reconciliation', 'opening-balance', accountId],
    () => reconciliationAPI.getOpeningBalanceCheck(accountId),
    { enabled: !!accountId }
  );

  const { data: drift, refetch: refetchDrift } = useQuery(
    ['reconciliation', 'drift', accountId],
    () => reconciliationAPI.getRunningBalanceDrift(accountId, { limit: 50 }),
    { enabled: !!accountId }
  );

  const createMutation = useMutation(
    () =>
      reconciliationAPI.createSession({
        bank_account_id: accountId,
        period_start: newSession.period_start,
        period_end: newSession.period_end,
        statement_closing_balance:
          newSession.statement_closing_balance === ''
            ? null
            : Number(newSession.statement_closing_balance),
        notes: newSession.notes || null,
      }),
    {
      onSuccess: (res) => {
        qc.invalidateQueries(['reconciliation', 'sessions', accountId]);
        if (res?.session?.id) setSelectedId(res.session.id);
      },
    }
  );

  const finaliseMutation = useMutation(
    ({ id, force, notes }) =>
      reconciliationAPI.finaliseSession(id, { force, notes: notes || undefined }),
    {
      onSuccess: () => {
        qc.invalidateQueries(['reconciliation', 'sessions', accountId]);
        setForceFinalise(false);
        setFinaliseNotes('');
      },
    }
  );

  const reopenMutation = useMutation(
    ({ id, reason }) => reconciliationAPI.reopenSession(id, { reason }),
    {
      onSuccess: () => {
        qc.invalidateQueries(['reconciliation', 'sessions', accountId]);
        setReopenReason('');
      },
    }
  );

  const obFixMutation = useMutation(
    (strategy) => reconciliationAPI.fixOpeningBalance(accountId, { strategy }),
    {
      onSuccess: () => {
        refetchOb();
      },
    }
  );

  const accountName =
    sessions[0]?.account_name ||
    obCheck?.bankAccount?.accountName ||
    drift?.bankAccount?.accountName ||
    'Account';

  return (
    <Layout>
      <div className="mx-auto max-w-6xl p-6">
        <div className="mb-4 flex items-center justify-between">
          <div>
            <Button
              variant="outline"
              size="sm"
              onClick={() =>
                navigate(`/tenant/banking/reconciliation/accounts/${accountId}`)
              }
              className="mb-2"
            >
              <ArrowLeft className="mr-1 h-4 w-4" /> Back to account workbench
            </Button>
            <h1 className="text-xl font-semibold text-gray-900 dark:text-white">
              Month-end close — {accountName}
            </h1>
            <p className="mt-1 text-xs text-gray-600 dark:text-gray-400">
              Sign-off a reconciled period, fix opening-balance mismatches and
              hunt down running-balance drift.
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={() => refetchSessions()}>
            <RefreshCw className="mr-1 h-4 w-4" /> Refresh
          </Button>
        </div>

        {/* ---------------------- Sessions list ---------------------- */}
        <Card className="mb-6">
          <CardContent className="p-4">
            <div className="mb-3 flex items-center gap-2">
              <ListChecks className="h-4 w-4 text-gray-500" />
              <h2 className="text-sm font-semibold text-gray-900 dark:text-white">
                Close sessions
              </h2>
            </div>

            {sessionsError && (
              <div className="mb-3 rounded border border-red-300 bg-red-50 px-3 py-2 text-xs text-red-700">
                {sessionsError.response?.data?.error ||
                  sessionsError.message ||
                  'Failed to load sessions'}
              </div>
            )}

            {loadingSessions ? (
              <div className="text-xs text-gray-500">Loading…</div>
            ) : sessions.length === 0 ? (
              <div className="text-xs text-gray-500">
                No close sessions yet. Use the form below to open one for the
                period you want to lock.
              </div>
            ) : (
              <div className="overflow-x-auto rounded border border-gray-100 dark:border-gray-800">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Period</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Statement</TableHead>
                      <TableHead className="text-right">System</TableHead>
                      <TableHead className="text-right">Diff</TableHead>
                      <TableHead>Finalised</TableHead>
                      <TableHead></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sessions.map((s) => (
                      <TableRow
                        key={s.id}
                        className={
                          selected?.id === s.id
                            ? 'bg-blue-50 dark:bg-blue-950/30'
                            : ''
                        }
                      >
                        <TableCell className="whitespace-nowrap">
                          {formatDate(s.period_start)} →{' '}
                          {formatDate(s.period_end)}
                        </TableCell>
                        <TableCell>
                          <Badge variant={STATUS_TONE[s.status] || 'secondary'}>
                            {s.locked ? (
                              <span className="inline-flex items-center gap-1">
                                <Lock className="h-3 w-3" /> {s.status}
                              </span>
                            ) : (
                              s.status
                            )}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {s.statement_closing_balance != null
                            ? currency(s.statement_closing_balance)
                            : '—'}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {s.system_closing_balance != null
                            ? currency(s.system_closing_balance)
                            : '—'}
                        </TableCell>
                        <TableCell
                          className={`text-right tabular-nums ${
                            s.difference != null &&
                            Math.abs(Number(s.difference)) > 0.01
                              ? 'text-amber-700'
                              : 'text-green-700'
                          }`}
                        >
                          {s.difference != null ? currency(s.difference) : '—'}
                        </TableCell>
                        <TableCell className="whitespace-nowrap text-xs text-gray-500">
                          {s.finalised_at ? formatDate(s.finalised_at) : '—'}
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setSelectedId(s.id)}
                          >
                            Select
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* ---------------------- Create / finalise ------------------ */}
        <div className="mb-6 grid grid-cols-1 gap-4 lg:grid-cols-2">
          <Card>
            <CardContent className="p-4">
              <h3 className="mb-3 text-sm font-semibold text-gray-900 dark:text-white">
                Start a new session
              </h3>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <FormLabel htmlFor="ms-start">Period start</FormLabel>
                  <Input
                    id="ms-start"
                    type="date"
                    value={newSession.period_start}
                    onChange={(e) =>
                      setNewSession((s) => ({
                        ...s,
                        period_start: e.target.value,
                      }))
                    }
                  />
                </div>
                <div>
                  <FormLabel htmlFor="ms-end">Period end</FormLabel>
                  <Input
                    id="ms-end"
                    type="date"
                    value={newSession.period_end}
                    onChange={(e) =>
                      setNewSession((s) => ({ ...s, period_end: e.target.value }))
                    }
                  />
                </div>
                <div className="col-span-2">
                  <FormLabel htmlFor="ms-stmt">
                    Statement closing balance (optional)
                  </FormLabel>
                  <Input
                    id="ms-stmt"
                    type="number"
                    step="0.01"
                    value={newSession.statement_closing_balance}
                    onChange={(e) =>
                      setNewSession((s) => ({
                        ...s,
                        statement_closing_balance: e.target.value,
                      }))
                    }
                    placeholder="e.g. 1234.56"
                  />
                </div>
                <div className="col-span-2">
                  <FormLabel htmlFor="ms-notes">Notes</FormLabel>
                  <Input
                    id="ms-notes"
                    type="text"
                    value={newSession.notes}
                    onChange={(e) =>
                      setNewSession((s) => ({ ...s, notes: e.target.value }))
                    }
                  />
                </div>
              </div>
              {createMutation.error && (
                <div className="mt-3 rounded border border-red-300 bg-red-50 px-3 py-2 text-xs text-red-700">
                  {createMutation.error.response?.data?.error ||
                    createMutation.error.message}
                </div>
              )}
              <div className="mt-3">
                <Button
                  size="sm"
                  onClick={() => createMutation.mutate()}
                  disabled={createMutation.isLoading}
                >
                  {createMutation.isLoading ? 'Opening…' : 'Open session'}
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <h3 className="mb-3 text-sm font-semibold text-gray-900 dark:text-white">
                {selected ? (
                  <>
                    Selected session:{' '}
                    <span className="text-gray-500">
                      {formatDate(selected.period_start)} →{' '}
                      {formatDate(selected.period_end)}
                    </span>
                  </>
                ) : (
                  'Selected session'
                )}
              </h3>

              {!selected ? (
                <div className="text-xs text-gray-500">
                  Select a session from the table above.
                </div>
              ) : selected.status === 'finalised' ? (
                <div>
                  <div className="mb-3 flex items-start gap-2 rounded border border-green-300 bg-green-50 px-3 py-2 text-xs text-green-800">
                    <Lock className="h-4 w-4 flex-shrink-0" />
                    <div>
                      Locked on {formatDate(selected.finalised_at)}. Matches in
                      this period are read-only until the session is reopened.
                    </div>
                  </div>
                  <FormLabel htmlFor="ms-reopen-reason">Reason</FormLabel>
                  <Input
                    id="ms-reopen-reason"
                    type="text"
                    value={reopenReason}
                    onChange={(e) => setReopenReason(e.target.value)}
                    placeholder="e.g. correcting misallocated supplier payment"
                  />
                  {reopenMutation.error && (
                    <div className="mt-2 rounded border border-red-300 bg-red-50 px-3 py-2 text-xs text-red-700">
                      {reopenMutation.error.response?.data?.error ||
                        reopenMutation.error.message}
                    </div>
                  )}
                  <div className="mt-3">
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={
                        reopenMutation.isLoading || reopenReason.trim().length === 0
                      }
                      onClick={() =>
                        reopenMutation.mutate({
                          id: selected.id,
                          reason: reopenReason.trim(),
                        })
                      }
                    >
                      <Unlock className="mr-1 h-4 w-4" />
                      {reopenMutation.isLoading ? 'Reopening…' : 'Reopen session'}
                    </Button>
                  </div>
                </div>
              ) : (
                <div>
                  <div className="mb-3 text-xs text-gray-600 dark:text-gray-400">
                    Finalising locks every match and statement line within the
                    period. The server will refuse if the statement/system
                    difference exceeds 0.01 or draft matches remain; tick{' '}
                    <span className="font-medium">Force</span> to override with
                    a documented variance.
                  </div>
                  <FormLabel htmlFor="ms-fin-notes">
                    Notes (saved onto the session)
                  </FormLabel>
                  <Input
                    id="ms-fin-notes"
                    type="text"
                    value={finaliseNotes}
                    onChange={(e) => setFinaliseNotes(e.target.value)}
                  />
                  <label className="mt-3 inline-flex items-center gap-2 text-xs text-gray-700 dark:text-gray-300">
                    <input
                      type="checkbox"
                      checked={forceFinalise}
                      onChange={(e) => setForceFinalise(e.target.checked)}
                    />
                    Force finalise with documented variance
                  </label>
                  {finaliseMutation.error && (
                    <div className="mt-2 rounded border border-red-300 bg-red-50 px-3 py-2 text-xs text-red-700">
                      {finaliseMutation.error.response?.data?.error ||
                        finaliseMutation.error.message}
                    </div>
                  )}
                  <div className="mt-3">
                    <Button
                      size="sm"
                      disabled={finaliseMutation.isLoading}
                      onClick={() =>
                        finaliseMutation.mutate({
                          id: selected.id,
                          force: forceFinalise,
                          notes: finaliseNotes,
                        })
                      }
                    >
                      <Lock className="mr-1 h-4 w-4" />
                      {finaliseMutation.isLoading
                        ? 'Finalising…'
                        : 'Finalise session'}
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* ---------------------- Opening balance wizard ------------ */}
        <Card className="mb-6">
          <CardContent className="p-4">
            <div className="mb-3 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Wand2 className="h-4 w-4 text-gray-500" />
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                  Opening-balance wizard
                </h3>
              </div>
              <Button variant="outline" size="sm" onClick={() => refetchOb()}>
                <RefreshCw className="mr-1 h-4 w-4" /> Recheck
              </Button>
            </div>

            {!obCheck ? (
              <div className="text-xs text-gray-500">Loading…</div>
            ) : !obCheck.hasStatementLines ? (
              <div className="text-xs text-gray-500">
                No statement lines imported on this account yet.
              </div>
            ) : !obCheck.hasRunningBalance ? (
              <div className="flex items-start gap-2 rounded border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                <AlertTriangle className="h-4 w-4 flex-shrink-0" />
                <div>
                  The earliest statement line has no running balance, so we
                  can&apos;t cross-check the opening balance automatically.
                  Re-import the statement with a balance column or update the
                  opening balance on the account manually.
                </div>
              </div>
            ) : !obCheck.needsReview ? (
              <div className="flex items-start gap-2 rounded border border-green-300 bg-green-50 px-3 py-2 text-xs text-green-800">
                <CheckCircle2 className="h-4 w-4 flex-shrink-0" />
                <div>
                  Opening balance matches the first imported line
                  ({currency(obCheck.recordedOpeningBalance)} on{' '}
                  {formatDate(obCheck.recordedOpeningBalanceDate)}).
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="flex items-start gap-2 rounded border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                  <AlertTriangle className="h-4 w-4 flex-shrink-0" />
                  <div>
                    <div>
                      Recorded opening balance:{' '}
                      <span className="font-medium">
                        {obCheck.recordedOpeningBalance == null
                          ? 'none'
                          : currency(obCheck.recordedOpeningBalance)}
                      </span>
                    </div>
                    <div>
                      Implied by first statement line (
                      {formatDate(obCheck.firstLine?.transaction_date)}):{' '}
                      <span className="font-medium">
                        {currency(obCheck.impliedOpeningBalance)}
                      </span>
                    </div>
                    <div>
                      Difference:{' '}
                      <span className="font-medium">
                        {currency(obCheck.difference)}
                      </span>
                    </div>
                  </div>
                </div>
                {obFixMutation.error && (
                  <div className="rounded border border-red-300 bg-red-50 px-3 py-2 text-xs text-red-700">
                    {obFixMutation.error.response?.data?.error ||
                      obFixMutation.error.message}
                  </div>
                )}
                <div className="flex flex-wrap gap-2">
                  <Button
                    size="sm"
                    onClick={() =>
                      obFixMutation.mutate('update_account_opening_balance')
                    }
                    disabled={obFixMutation.isLoading}
                  >
                    Use implied opening balance
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      obFixMutation.mutate('flag_first_line_for_review')
                    }
                    disabled={obFixMutation.isLoading}
                  >
                    Flag first line for manual review
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* ---------------------- Drift detector -------------------- */}
        <Card>
          <CardContent className="p-4">
            <div className="mb-3 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-gray-500" />
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                  Running-balance drift
                </h3>
              </div>
              <Button variant="outline" size="sm" onClick={() => refetchDrift()}>
                <RefreshCw className="mr-1 h-4 w-4" /> Rescan
              </Button>
            </div>

            {!drift ? (
              <div className="text-xs text-gray-500">Loading…</div>
            ) : !drift.hasDrift ? (
              <div className="flex items-start gap-2 rounded border border-green-300 bg-green-50 px-3 py-2 text-xs text-green-800">
                <CheckCircle2 className="h-4 w-4 flex-shrink-0" />
                <div>
                  No running-balance drift across {drift.scannedCount}{' '}
                  statement lines on this account.
                </div>
              </div>
            ) : (
              <div>
                <div className="mb-2 text-xs text-amber-700">
                  {drift.driftCount} line(s) out of {drift.scannedCount}{' '}
                  scanned show a running-balance drift. First offender:{' '}
                  {formatDate(drift.firstDriftAt)}.
                </div>
                <div className="overflow-x-auto rounded border border-gray-100 dark:border-gray-800">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Date</TableHead>
                        <TableHead>Description</TableHead>
                        <TableHead className="text-right">Amount</TableHead>
                        <TableHead className="text-right">Reported</TableHead>
                        <TableHead className="text-right">Expected</TableHead>
                        <TableHead className="text-right">Drift</TableHead>
                        <TableHead></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {drift.drifts.map((d) => (
                        <TableRow key={d.line_id}>
                          <TableCell className="whitespace-nowrap">
                            {formatDate(d.transaction_date)}
                          </TableCell>
                          <TableCell className="max-w-xs truncate">
                            {d.description || d.reference || '—'}
                          </TableCell>
                          <TableCell className="text-right tabular-nums">
                            {currency(d.amount)}
                          </TableCell>
                          <TableCell className="text-right tabular-nums">
                            {currency(d.running_balance)}
                          </TableCell>
                          <TableCell className="text-right tabular-nums">
                            {currency(d.expected_running_balance)}
                          </TableCell>
                          <TableCell className="text-right tabular-nums text-amber-700">
                            {currency(d.drift)}
                          </TableCell>
                          <TableCell>
                            <Link
                              to={`/tenant/banking/reconciliation/accounts/${accountId}?line=${d.line_id}`}
                              className="text-xs text-blue-600 hover:underline"
                            >
                              Open
                            </Link>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
                {drift.truncated && (
                  <div className="mt-2 text-[11px] text-gray-500">
                    Showing the first {drift.drifts.length} drift rows.
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
